import { useEffect, useState } from "react";
import axios from "axios";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const merchantTypeOptions = [
  { value: "BLACKFIT", label: "Blackfit" },
  { value: "BLACKFIT_PREMIERE", label: "Blackfit Premiere" },
];
const ptTypeOptions = [
  { value: "PTS", label: "PTS" },
  { value: "PTC", label: "PTC" },
  { value: "PTT", label: "PTT" },
];

const money = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(v || 0));

const initialForm = {
  merchant_type: "",
  pt_type: "",
  rate_per_session: "",
  effective_from: "",
};

export default function PtSessionRates() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/pt-session-rates`, { withCredentials: true });
      setRates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch pt session rates:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data rate.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, []);

  const openCreate = () => {
    setEditingRate(null);
    setFormData({ ...initialForm, effective_from: new Date().toISOString().slice(0, 10) });
    setIsModalOpen(true);
  };

  const openEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      merchant_type: String(rate.merchant_type || ""),
      pt_type: String(rate.pt_type || ""),
      rate_per_session: String(rate.rate_per_session ?? ""),
      effective_from: String(rate.effective_from || "").slice(0, 10),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm("Hapus rate ini?")) return;
    try {
      await axios.delete(`${API}/pt-session-rates/${rateId}`, { withCredentials: true });
      fetchRates();
    } catch (error) {
      console.error("Failed to delete rate:", error);
      alert(error.response?.data?.detail || "Gagal menghapus rate.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      merchant_type: String(formData.merchant_type || "").trim().toUpperCase(),
      pt_type: String(formData.pt_type || "").trim().toUpperCase(),
      rate_per_session: Number(formData.rate_per_session || 0),
      effective_from: String(formData.effective_from || "").trim(),
    };
    if (!payload.merchant_type || !payload.pt_type || !payload.effective_from) {
      alert("Merchant type, PT type, dan Effective From wajib diisi.");
      return;
    }
    try {
      if (editingRate) {
        await axios.put(`${API}/pt-session-rates/${editingRate.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/pt-session-rates`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingRate(null);
      setFormData(initialForm);
      fetchRates();
    } catch (error) {
      console.error("Failed to save rate:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan rate.");
    }
  };

  const sortedRates = [...rates].sort((a, b) => {
    if (a.merchant_type !== b.merchant_type) return a.merchant_type.localeCompare(b.merchant_type);
    if (a.pt_type !== b.pt_type) return a.pt_type.localeCompare(b.pt_type);
    return (b.effective_from || "").localeCompare(a.effective_from || "");
  });

  return (
    <DashboardLayout>
      <Header title="Rate Sesi PT" />
      <div className="space-y-6 p-8" data-testid="pt-session-rates-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Rate Sesi PT per Tipe</h2>
              <p className="mt-1 text-sm text-zinc-500">Kelola harga per sesi done PT untuk pembagian hasil. Perubahan berlaku mulai tanggal effective_from.</p>
            </div>
            <Button onClick={openCreate} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah Rate
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Merchant Type</TableHead>
                <TableHead>PT Type</TableHead>
                <TableHead className="text-right">Rate/Sesi Done</TableHead>
                <TableHead>Effective From</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">Loading...</TableCell>
                </TableRow>
              ) : sortedRates.length > 0 ? (
                sortedRates.map((rate, index) => (
                  <TableRow key={rate.id || index} className="border-white/5 hover:bg-zinc-900/50">
                    <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-4 text-sm font-medium text-white">{rate.merchant_type === "BLACKFIT_PREMIERE" ? "Blackfit Premiere" : "Blackfit"}</TableCell>
                    <TableCell className="py-4 text-sm font-medium text-white">{rate.pt_type}</TableCell>
                    <TableCell className="py-4 text-right text-sm tabular-nums text-[#D4AF37]">{money(rate.rate_per_session)}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{rate.effective_from}</TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => openEdit(rate)} className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white">
                          <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <button type="button" onClick={() => handleDelete(rate.id)} className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400">
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">Belum ada data rate.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="border-transparent bg-zinc-900 text-white shadow-2xl ring-1 ring-white/5 sm:max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editingRate ? "Edit Rate Sesi PT" : "Tambah Rate Sesi PT"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Merchant Type</Label>
              <Select value={formData.merchant_type} onValueChange={(v) => setFormData({ ...formData, merchant_type: v })}>
                <SelectTrigger className="rounded-sm border-transparent bg-white/[0.04] text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15"><SelectValue placeholder="Pilih merchant" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  {merchantTypeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">PT Type</Label>
              <Select value={formData.pt_type} onValueChange={(v) => setFormData({ ...formData, pt_type: v })}>
                <SelectTrigger className="rounded-sm border-transparent bg-white/[0.04] text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15"><SelectValue placeholder="Pilih tipe" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  {ptTypeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Rate per Sesi Done (Rp)</Label>
              <Input type="number" value={formData.rate_per_session} onChange={(e) => setFormData({ ...formData, rate_per_session: e.target.value })} placeholder="80000" className="rounded-sm border-transparent bg-white/[0.04] text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Effective From (mulai berlaku)</Label>
              <Input type="date" value={formData.effective_from} onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })} className="rounded-sm border-transparent bg-white/[0.04] text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15" />
              <p className="text-[11px] text-zinc-500">Laporan bulanan akan menggunakan rate ini untuk bulan yang tanggalnya {'>='} effective_from.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" onClick={() => setIsModalOpen(false)} className="rounded-sm bg-white/[0.03] text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]">Batal</Button>
              <Button type="submit" className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">Simpan</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
